import { ActivePlanStageOverview } from "../../../../../../types/healthcare/TreatmentPlanTypes";

/**
 * Dashboard counterpart to the modal's watcher merge.
 *
 * The peptide dashboard hides watcher stages and colours each remaining stage by its own status,
 * so an action that has run shows green even while the plan is actually paused on the watcher that
 * follows it. The dashboard has no canvas edges, but every stage carries its `configuration`, so we
 * link a still-waiting watcher to the action that produced the thing it watches (e.g. the
 * `assign_form` action for the form the watcher waits on) and surface the watcher's waiting state on
 * that action — matching what the plan modal shows when opened.
 */

const WAITING_STATUSES = new Set(["waiting", "in_progress"]);

/** Resource tokens a watcher is waiting on (form / medication). */
function watchedTokens(stage: ActivePlanStageOverview): string[] {
  const cfg = (stage.configuration ?? {}) as Record<string, any>;
  const items: Record<string, any>[] = Array.isArray(cfg.watcher_items)
    ? cfg.watcher_items
    : cfg.watch_config || cfg.watcher_type
      ? [{ watcher_type: cfg.watcher_type, watch_config: cfg.watch_config }]
      : [];

  const tokens: string[] = [];
  for (const it of items) {
    const wc = (it.watch_config ?? {}) as Record<string, any>;
    if (it.watcher_type === "form_completed" && wc.form_id != null) tokens.push(`form:${wc.form_id}`);
    if (it.watcher_type === "medication_script_uploaded") {
      if (wc.medication_id != null) tokens.push(`med:${wc.medication_id}`);
      else if (wc.medication_name) tokens.push(`med:${String(wc.medication_name).toLowerCase()}`);
    }
  }
  return tokens;
}

/** Resource tokens an action stage produced (assigned a form / medication). */
function producedTokens(stage: ActivePlanStageOverview): string[] {
  const cfg = (stage.configuration ?? {}) as Record<string, any>;
  const actions: Record<string, any>[] = Array.isArray(cfg.actions) ? cfg.actions : [];

  const tokens: string[] = [];
  for (const a of actions) {
    const c = (a.config ?? {}) as Record<string, any>;
    if (a.action_type === "assign_form" && c.form_id != null) tokens.push(`form:${c.form_id}`);
    if (a.action_type === "assign_medication") {
      if (c.medication_id != null) tokens.push(`med:${c.medication_id}`);
      else if (c.medication_name) tokens.push(`med:${String(c.medication_name).toLowerCase()}`);
    }
  }
  return tokens;
}

/**
 * Map of snapshot_id → effective status, overriding a completed action with its joined watcher's
 * waiting status. Keyed on `snapshot_id` (not `stage_key`) so one attempt's override never bleeds
 * onto the other attempts of the same looped stage. Only stages whose status changes are included;
 * callers fall back to the stage's own status otherwise.
 */
export function computeEffectiveStageStatuses(stages: ActivePlanStageOverview[]): Map<number, string> {
  const effective = new Map<number, string>();

  const waitingWatchers = stages.filter(
    (s) => s.stage_type === "watcher" && WAITING_STATUSES.has(s.status),
  );
  if (waitingWatchers.length === 0) return effective;

  const candidateActions = stages.filter((s) => s.stage_type !== "watcher" && s.status === "completed");

  for (const watcher of waitingWatchers) {
    const watched = new Set(watchedTokens(watcher));
    if (watched.size === 0) continue;
    for (const action of candidateActions) {
      // Pair within the same loop iteration so attempt-0's satisfied watcher never re-colours
      // attempt-1's action (and vice versa).
      if ((action.loop_iteration ?? 0) !== (watcher.loop_iteration ?? 0)) continue;
      if (producedTokens(action).some((t) => watched.has(t))) {
        effective.set(action.snapshot_id, watcher.status);
      }
    }
  }

  return effective;
}
